import React from "react";
import Icon from "./Icon";
import { PixelArt } from "./ProfileBits";
import {
  AvatarConfig,
  BODY_COLORS,
  CRITTERS,
  EXPRESSIONS,
  FACE_EXTRAS,
  HAIRSTYLES,
  HAIR_COLORS,
  HATS,
  Layer,
  SKIN_ORDER,
  SKIN_TONES,
} from "../avatars";

type Options = Record<string, { label: string; layer: Layer }>;

// Build-your-own pixel character: every choice shows a tiny preview of
// *your* character with that option applied.
export default function AvatarMaker({
  value,
  onChange,
}: {
  value: AvatarConfig;
  onChange: (next: AvatarConfig) => void;
}) {
  const update = (patch: Partial<AvatarConfig>) => onChange({ ...value, ...patch });

  const surprise = () => {
    const any = <T,>(list: T[]) => list[Math.floor(Math.random() * list.length)];
    const index = (list: unknown[]) => Math.floor(Math.random() * list.length);
    onChange({
      ...value,
      skin: index(SKIN_TONES),
      hair: any(Object.keys(HAIRSTYLES)),
      hairColor: index(HAIR_COLORS),
      expression: any(Object.keys(EXPRESSIONS)),
      hat: Math.random() < 0.5 ? "none" : any(Object.keys(HATS)),
      extra: Math.random() < 0.5 ? "none" : any(Object.keys(FACE_EXTRAS)),
      shirt: index(BODY_COLORS),
      critter: any(Object.keys(CRITTERS)),
      body: index(BODY_COLORS),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="btn-row">
        <div className="seg" role="group" aria-label="Kind of avatar">
          {(["person", "critter"] as const).map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={value.kind === k}
              onClick={() => update({ kind: k })}
            >
              {k === "person" ? "Person" : "Critter"}
            </button>
          ))}
        </div>
        <button type="button" className="btn btn-plain btn-sm" onClick={surprise}>
          <Icon name="zap" size={14} /> Surprise me
        </button>
      </div>

      {value.kind === "person" ? (
        <>
          <Swatches
            label="Skin"
            colors={SKIN_TONES}
            order={SKIN_ORDER}
            value={value.skin}
            onPick={(skin) => update({ skin })}
          />
          <Thumbs
            label="Hair"
            options={HAIRSTYLES}
            value={value.hair}
            preview={(hair) => ({ ...value, hair })}
            onPick={(hair) => update({ hair })}
          />
          <Swatches
            label="Hair color"
            colors={HAIR_COLORS}
            value={value.hairColor}
            onPick={(hairColor) => update({ hairColor })}
          />
          <Thumbs
            label="Expression"
            options={EXPRESSIONS}
            value={value.expression}
            preview={(expression) => ({ ...value, expression })}
            onPick={(expression) => update({ expression })}
          />
          <Thumbs
            label="Hat & accessories"
            options={HATS}
            value={value.hat}
            preview={(hat) => ({ ...value, hat })}
            onPick={(hat) => update({ hat })}
          />
          <Thumbs
            label="Face details"
            options={FACE_EXTRAS}
            value={value.extra}
            preview={(extra) => ({ ...value, extra })}
            onPick={(extra) => update({ extra })}
          />
          <Swatches
            label="Shirt"
            colors={BODY_COLORS}
            value={value.shirt}
            onPick={(shirt) => update({ shirt })}
          />
        </>
      ) : (
        <>
          <Thumbs
            label="Critter"
            options={CRITTERS}
            value={value.critter}
            preview={(critter) => ({ ...value, critter })}
            onPick={(critter) => update({ critter })}
          />
          <Swatches
            label="Color"
            colors={BODY_COLORS}
            value={value.body}
            onPick={(body) => update({ body })}
          />
          <Thumbs
            label="Hat & accessories"
            options={HATS}
            value={value.hat}
            preview={(hat) => ({ ...value, hat })}
            onPick={(hat) => update({ hat })}
          />
        </>
      )}
    </div>
  );
}

function Thumbs({
  label,
  options,
  value,
  preview,
  onPick,
}: {
  label: string;
  options: Options;
  value: string;
  preview: (key: string) => AvatarConfig;
  onPick: (key: string) => void;
}) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <div className="pick-grid" role="group" aria-label={label}>
        {Object.entries(options).map(([key, opt]) => (
          <button
            key={key}
            type="button"
            className="pick thumb"
            aria-pressed={value === key}
            aria-label={opt.label}
            title={opt.label}
            onClick={() => onPick(key)}
          >
            <PixelArt config={preview(key)} size={40} />
            <span className="thumb-label">{opt.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Swatches({
  label,
  colors,
  order,
  value,
  onPick,
}: {
  label: string;
  colors: string[];
  order?: number[]; // display order, as indexes into colors
  value: number;
  onPick: (index: number) => void;
}) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <div className="flex flex-wrap gap-1" role="group" aria-label={label}>
        {(order || colors.map((_, i) => i)).map((i) => (
          <button
            key={colors[i]}
            type="button"
            className="pick !p-1"
            aria-pressed={value === i}
            aria-label={`${label} ${i + 1}`}
            onClick={() => onPick(i)}
          >
            <span className="swatch" style={{ background: colors[i] }} />
          </button>
        ))}
      </div>
    </div>
  );
}
